import React, { useState, useCallback, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useIntersectionObserver } from 'hooks/useIntersectionObserver';
import { QaIdentifier, useQa } from 'hooks/useQa';

import {
    showScrollView,
    showVisibleFilter,
} from '../__helper/showVisibleFilter';
import {
    FILTER_SCROLL_TOP_ID,
    intersectionTopObserverOptions,
    intersectionEndObserverOptions,
} from '../config';

import styles from './../StickyFilter.module.scss';

interface SentinelProps {
    children: React.ReactNode;
}

const Sentinel = ({ children }: SentinelProps) => {
    const { t } = useTranslation();
    const qaAttr = useQa(QaIdentifier.Search_Filter_Sticky_Container);
    const [render, setRender] = useState(false);
    const scrollViewList = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        if (render && scrollViewList.current) {
            showVisibleFilter(scrollViewList.current);
        }
    }, [render]);

    const observerCallback = useCallback(
        (show: boolean) => {
            setRender(show);
        },
        [setRender]
    );

    const sentinelRef = useIntersectionObserver(
        intersectionTopObserverOptions,
        () => observerCallback(true),
        () => observerCallback(false)
    );

    const observerEndCallback = useCallback((show: boolean) => {
        if (scrollViewList.current) {
            showScrollView(scrollViewList.current, show);
        }
    }, []);

    const sentinelEndRef = useIntersectionObserver(
        intersectionEndObserverOptions,
        () => observerEndCallback(false),
        () => observerEndCallback(false),
        () => observerEndCallback(true)
    );

    const scrollToTop = useCallback(() => {
        const filterTop = document.getElementById(FILTER_SCROLL_TOP_ID);
        if (filterTop) {
            window.scrollTo({
                top: filterTop?.offsetTop,
                behavior: 'smooth',
            });
        }
    }, []);

    return (
        <div ref={sentinelRef} className={styles.SentinelWrapper}>
            {render && (
                <div
                    {...qaAttr}
                    className={styles.StickyFilter}
                    ref={scrollViewList}
                >
                    <header>
                        {t('TRANS-popular-filters')}
                        <span onClick={scrollToTop}>{t('TRANS-show-all')}</span>
                    </header>
                    {children}
                </div>
            )}
            <div ref={sentinelEndRef} className={styles.EndOfList} />
        </div>
    );
};

export default Sentinel;
